if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isD2cControlled = this.dataset.d2cControlled === 'true';
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.isD2cControlled) this.bindD2cGalleryControls();
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        if (this.isD2cControlled) this.updateD2cGalleryCounter(mediaId);
      }

      bindD2cGalleryControls() {
        this.elements.viewer
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) =>
            button.addEventListener(
              'click',
              (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.goToD2cGalleryMedia(button.name === 'next' ? 1 : -1);
              },
              true
            )
          );
        this.elements.viewer.slider?.addEventListener(
          'scroll',
          debounce(() => this.updateD2cGalleryCounter(), 100)
        );
        window.requestAnimationFrame(() => {
          this.setInitialD2cMobileMedia();
          this.updateD2cGalleryCounter();
        });
        window.setTimeout(() => this.updateD2cGalleryCounter(), 250);
      }

      getD2cGalleryTargets() {
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          .map((thumbnail, index) => {
            const media = this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`);
            const order = media ? parseInt(window.getComputedStyle(media).order, 10) || 0 : 0;
            return { thumbnail, media, index, order };
          })
          .filter((item) => item.media)
          .sort((a, b) => a.order - b.order || a.index - b.index)
          .map((item) => item.thumbnail);
      }

      getD2cActiveIndex(targets = this.getD2cGalleryTargets()) {
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeMediaId = activeMedia?.dataset?.mediaId;
        const activeIndex = targets.findIndex((thumbnail) => thumbnail.dataset.target === activeMediaId);
        if (activeIndex >= 0) return activeIndex;
        const activeThumbnailIndex = targets.findIndex((thumbnail) =>
          thumbnail.querySelector('button')?.hasAttribute('aria-current')
        );
        return Math.max(activeThumbnailIndex, 0);
      }

      goToD2cGalleryMedia(direction) {
        const targets = this.getD2cGalleryTargets();
        if (targets.length < 2) return;
        const activeIndex = this.getD2cActiveIndex(targets);
        const nextIndex = (activeIndex + direction + targets.length) % targets.length;
        this.setActiveMedia(targets[nextIndex].dataset.target, false);
      }

      setInitialD2cMobileMedia() {
        if (this.mql.matches) return;
        const targets = this.getD2cGalleryTargets();
        const firstTarget = targets[0]?.dataset?.target;
        const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset?.mediaId;
        if (firstTarget && firstTarget !== activeMediaId) this.setActiveMedia(firstTarget, false);
      }

      updateD2cGalleryCounter(mediaId) {
        const targets = this.getD2cGalleryTargets();
        if (!targets.length) return;
        const activeIndex = mediaId
          ? targets.findIndex((thumbnail) => thumbnail.dataset.target === mediaId)
          : this.getD2cActiveIndex(targets);
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (current) current.textContent = Math.max(activeIndex, 0) + 1;
        if (total) total.textContent = targets.length;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
